import React, {Fragment, useEffect, useState} from 'react';
import { Menu, Transition } from '@headlessui/react';
import {getSurveyAssessments} from './fetchers';
import {fetchApi, getHeaders} from './utils/fetch';
import {classNames} from './utils/classNames';
import {ChevronDownIcon} from '@heroicons/react/20/solid';
import {
  ArrowDownCircleIcon,
  ArrowDownTrayIcon, ArrowPathIcon, HandRaisedIcon, LinkIcon,
  NoSymbolIcon,
  PaperAirplaneIcon, PencilSquareIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import {parseSurveyState} from './utils/state';
import {useNotifications} from './contexts/ContextNotification';
import {Spinner} from './Spinner';
import {Button} from './Button';
import SwitchToggle from './SwitchToggle';
import {useSurvey} from './contexts/ContextSurvey';
import ModalAddParticipant from './ModalAddParticipant';
import ModalSurveyCheckout from './ModalSurveyCheckout';
import ModalAlert from './ModalAlert';
import CoinsIcon from "./icons/CoinsIcon";
import {BanknotesIcon} from "@heroicons/react/24/solid";
import Tippy from "@tippyjs/react";
import {useUser} from "./contexts/ContextUser";
import {AdminRequired} from "./App";
import CoachEmailsEditor from './CoachEmailsEditor';

let liveReloadAssessmentsInt;

export default function SurveyAssessments({assessments, liveReload, minimizedView = false}) {
  const {user: {admin: isUserAdmin, account: {tokens_available: tokens}}} = useUser();
  const [editAssessment, setEditAssessment] = useState({});
  const [updatedAssessments, setUpdatedAssessments] = useState(null);
  const [showParticipant, setShowParticipant] = useState(false);
  const [modalAlert, setModalAlert] = useState({title: 'Your Report isn\'t ready yet.', body: 'Please try again in a few seconds!', confirmButtonText: 'Close', open: false});
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [downloadingTeamReport] = useState(false);
  const {addNotification} = useNotifications();
  const {survey, updateSurvey, loadSurvey} = useSurvey();

  assessments = updatedAssessments || assessments;

  useEffect(() => {
    if (liveReload) {
      liveReloadAssessmentsInt = setInterval(loadAssessments, 5000);
    } else if (!liveReload) {
      clearInterval(liveReloadAssessmentsInt);
    }
  }, [liveReload]);

  function loadAssessments() {
    return getSurveyAssessments(null, survey.id)
      .then((response) => {
        setUpdatedAssessments(response);
      });
  }

  async function updateParticipant(id, event) {
    let response;
    try {
      response = await fetchApi(`/api/v1/internal/participants/${id}`, { method: 'PATCH', body: JSON.stringify({[event.target.name]: event.target.value})})
    } catch (error) {
      // The request itself failed (network, or a body that is not JSON): nothing was saved.
      response = {success: false, error: 'The change could not be saved. Please try again.'};
    }
    // Refused (#523: the API refuses an email edit once an invitation or answers exist), or not
    // sent. Put the stored value back and say why, instead of leaving the refused one showing.
    if (response?.success === false) {
      event.target.value = event.target.defaultValue;
      addNotification({title: 'Participant not updated', type: 'failure', body: response.error || 'The change could not be saved.'});
    }
    loadAssessments();
  }

  async function updateAssessment(id, key, {target}) {
    await fetchApi(`/api/v1/internal/surveys/${survey.id}/assessments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        [key]: target.value,
      }),
    });

    loadAssessments();
  }

  function disableAssessment(assessment) {
    const {id, disabled, participant} = assessment;
    fetchApi(`/api/v1/internal/surveys/${survey.id}/assessments/${id}/disable`, {method: 'PATCH', body: JSON.stringify({
      disable: !disabled,
    })})
    .then(() => addNotification({title: 'Assessment updated!', body: `Assessment for ${participant.first_name} ${participant.last_name} has been ${!disabled ? 'disabled' : 'enabled'}.`}))
    .finally(loadAssessments);
  }

  function syncAssessment(assessment) {
    const {id} = assessment;

    fetchApi(`/api/v1/internal/surveys/${survey.id}/assessments/${id}/sync`, {method: 'POST'})
      .then(({success}) => {
        if (success) {
          addNotification({
            title: 'Assessment has been synced',
            body: 'Please wait a few seconds for the assessment status to be reflected below.'
          });
        } else {
          addNotification({
            title: 'Assessment did NOT sync',
            type: 'failure',
            body: 'This is likely because the assessment was not in the correct state of "completion_pending" so we did not proceed with the sync.'
          });
        }
      })
      .finally(loadAssessments);
  }

  function removeAssessment(assessment) {
    const {id, participant} = assessment;
    fetchApi(`/api/v1/internal/surveys/${survey.id}/assessments/${id}`, {method: 'DELETE'})
      .then(({success, msg}) => {
        if (success) {
          addNotification({
            title: 'Assessment removed.',
            body: `Assessment for ${participant.email} has been removed from this survey.`
          });
        } else {
          addNotification({
            title: 'Assessment was not removed',
            type: 'failure',
            body: msg,
          })
        }
      })
      .finally(loadAssessments);
  }

  function createNewParticipant() {
    loadAssessments();
    setShowParticipant(false);
  }

  function resentInvitationEmail(assessmentId) {
    fetchApi(`/api/v1/internal/surveys/${survey.id}/assessments/${assessmentId}/resend_invite`, {
      method: 'POST',
    }).then(loadAssessments);
  }

  function downloadReport(assessment) {
    window.open(assessment.download_results, '_self')
    addNotification({title: 'Downloading results...', body: `Downloading results for ${assessment.participant.first_name} ${assessment.participant.last_name}.`})
  }

  // Reports what the server did (#621): refused with a reason, failed to
  // queue, or queued. Delivery itself shows up in the Results column.
  // Per-row Send Results follows the survey's settings, like bulk (#621 fix
  // 6): the server sends to whoever results_to_send names, after a
  // confirmation that says who that is.
  function sendResults({id, participant}) {
    setRowPrompt(null);
    fetchApi(`/api/v1/internal/surveys/${survey.id}/assessments/${id}/send_results`, {method: 'POST'})
      .then((response) => {
        if (response?.success && !resultsPdfSurvey) {
          // Not a results PDF (cq_product acknowledgement, behavioral or
          // manager-review completion email): as at base.
          addNotification({title: 'Sending results', body: `Sending results to participant: ${participant.email}.`});
        } else if (response?.success) {
          const queued = (response.sends || []).filter((send) => send.outcome === 'queued');
          const notSent = (response.sends || []).filter((send) => send.outcome === 'failed');
          if (notSent.length) {
            // Some queued, some not (#621 fix wave 5): name both, the way bulk
            // reports a mixed send.
            const reasons = [...new Set(notSent.map((send) => send.error).filter(Boolean))];
            addNotification({
              title: 'Some results not sent',
              type: 'failure',
              body: `Queued ${pluralEmails(queued.length)} (${recipientList(queued)}). ` +
                `${pluralEmails(notSent.length)} could not be sent (${recipientList(notSent)}): ${reasons.join(' ')}`,
            });
            return;
          }
          // When only coach copies were queued, say so and for whom (#621 fix
          // wave 3, item D9); the participant was not emailed.
          const coachesOnly = queued.length > 0 && !queued.some((send) => send.role === 'participant');
          addNotification(coachesOnly
            ? {title: 'Results queued', body: `Coach copies of ${participant.first_name} ${participant.last_name}'s results are queued for ${queued.map((send) => send.email).join(', ')}. The Results column shows when they are sent.`}
            : {title: 'Results queued', body: `Results for ${participant.email} are queued. The Results column shows when they are sent.`});
        } else {
          addNotification({title: 'Results not sent', type: 'failure', body: response?.error || 'The results email could not be queued.'});
        }
      })
      .finally(loadAssessments);
  }

  function rowPromptText({participant, results_to_send: roles}) {
    if (!roles.includes('participant')) {
      return `Send coach copies of ${participant.first_name} ${participant.last_name}'s results to ${coachList}? ${participant.first_name} is not emailed.`;
    }
    const own = `Send the results email to ${participant.email}?`;
    return roles.includes('coach') ? `${own} Coach copies go to ${coachList}.` : own;
  }

  const surveyState = parseSurveyState(survey);

  // Bulk Send Results (#621, D3; PI ruling 2026-10-01: it follows the survey's
  // settings). The server says, per completion, who is still owed a results
  // email under the current settings (results_to_send: 'participant' and/or
  // 'coach'); it decides again when the send runs.
  const sendable = assessments.filter((a) => a.results_to_send?.length);
  const unsentCount = sendable.length;
  const participantSendCount = sendable.filter((a) => a.results_to_send.includes('participant')).length;
  const coachSendCount = sendable.filter((a) => a.results_to_send.includes('coach')).length;
  const coachList = (survey.coach_emails || []).join(', ');
  // Participants owed their own results email were the survey to send them:
  // the server decides, through the same rule as bulk (I2).
  const participantsWaiting = assessments.filter((a) => a.participant_results_owed).length;
  // Results controls exist only where results are a PDF this app emails: not
  // on a cq_product survey, whose results are the dashboard acknowledgement
  // (#621 fix 7).
  const resultsPdfSurvey = survey.survey_type === 'coachability' && !survey.cq_product;
  const canBulkSend = surveyState.resultDownloadsEnabled && resultsPdfSurvey;
  const [bulkPrompt, setBulkPrompt] = useState(null); // null, 'send' or 'auto-send'
  const [rowPrompt, setRowPrompt] = useState(null); // the assessment a per-row send is confirming
  const [bulkSending, setBulkSending] = useState(false);

  // Auto-send applies to completions from now on (acceptance criterion 5), so
  // switching it on while participants wait prompts the partner to send theirs.
  // The prompt waits for the setting to be saved, because the send follows it.
  // The setting changes what each completion is owed, and a closed survey is
  // not polled, so the assessments are reloaded whatever the partner answers
  // (#621 fix wave 3, item D12).
  function changeAutoSendResults(auto_send_results) {
    Promise.resolve(updateSurvey({auto_send_results})).then(() => {
      loadAssessments();
      if (auto_send_results && canBulkSend && participantsWaiting > 0) setBulkPrompt('auto-send');
    });
  }

  function bulkPromptText() {
    const coachCopies = `Coach copies of the results for ${coachSendCount} completed ${coachSendCount === 1 ? 'participant' : 'participants'} go to ${coachList}.`;

    if (bulkPrompt === 'auto-send') {
      const autoSend = `Auto Send Results applies to participants who complete from now on. ${pluralParticipants(participantsWaiting)} already completed and have not been sent their results. Send them now?`;
      // The same send carries any coach copies still owed, so name them too.
      return coachSendCount ? `${autoSend} ${coachCopies}` : autoSend;
    }
    if (!participantSendCount) {
      return `Send coach copies of the results for ${coachSendCount} completed ${coachSendCount === 1 ? 'participant' : 'participants'} to ${coachList}? Participants are not emailed.`;
    }

    const participants = `Send the results email to ${participantSendCount} completed ${participantSendCount === 1 ? 'participant' : 'participants'} whose results have not been sent?`;
    return coachSendCount ? `${participants} ${coachCopies}` : participants;
  }

  function bulkSendResults() {
    setBulkSending(true);
    fetchApi(`/api/v1/internal/surveys/${survey.id}/send_results`, {method: 'POST'})
      .then((response) => {
        if (!response?.success) {
          addNotification({title: 'Results not sent', type: 'failure', body: response?.error || 'Send Results could not run.'});
          return;
        }

        // Counted in emails, not completions: one completion can mean a
        // participant email and several coach copies (#621 fix 9).
        const sends = response.outcomes.flatMap((o) => o.sends || []);
        const queuedEmails = sends.filter((s) => s.outcome === 'queued').length;
        const failedSends = sends.filter((s) => s.outcome === 'failed');
        const reasons = [...new Set(failedSends.map((s) => s.error).filter(Boolean))];
        addNotification({
          title: failedSends.length ? 'Some results not sent' : 'Results queued',
          type: failedSends.length ? 'failure' : undefined,
          body: `Queued ${pluralEmails(queuedEmails)}.` +
            (failedSends.length ? ` ${pluralEmails(failedSends.length)} could not be sent: ${reasons.join(' ')}` : ''),
        });
      })
      .finally(() => {
        setBulkSending(false);
        setBulkPrompt(null);
        loadAssessments();
      });
  }

  return (
    <>
      <ModalAlert headerIcon={(
        <div className="mx-auto flex h-12 w-12 p-3 items-center justify-center rounded-full bg-yellow-100 border-2 border-yellow-500">
          <HandRaisedIcon className='w-10 h-10 text-yellow-500' />
        </div>
      )} alert={modalAlert} setOpen={(open) => setModalAlert({...modalAlert, open})} open={modalAlert.open} />
      <ModalSurveyCheckout onCheckoutComplete={() => {
        loadSurvey();
        loadAssessments();
      }} purchaseQuantity={1} availableTokens={tokens.team_report} survey={survey} setOpen={(open) => setShowCheckoutModal(open)} open={showCheckoutModal} url={`/api/v1/internal/surveys/${survey.id}/checkout_team_report`} />
      <div className="pb-12">
        <div className='sm:flex sm:items-center'>
          <div className="flex justify-between items-center w-full">
            <div className="sm:flex-auto">
              <h1 className="text-xl font-semibold text-gray-900">Assessments</h1>
              <p className="mt-2 text-sm text-gray-700">
                A list of all assessments attached to this survey.
              </p>
            </div>
          </div>

          { surveyState.canAddParticipants && (
            <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
              <div>
                <button
                  onClick={(() => setShowParticipant(true))}
                  className={classNames(
                    'inline-flex items-center justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto',
                    'bg-blue-600 text-white hover:bg-blue-700'
                  )}
                >
                  <UserPlusIcon className="h-5 w-5 text-white mr-2" />
                  Add Participant
                </button>
              </div>
              <ModalAddParticipant surveyId={survey.id} onClose={() => setShowParticipant(false)} open={showParticipant} onSave={createNewParticipant} />
            </div>
          ) }
        </div>
        <div className="flex items-center justify-between space-x-1 my-4">
          <div className="flex justify-between items-center">
            { surveyState.shouldLiveReload && (
              <>
                <span className="mr-1">Live</span>
                <Spinner textColor='text-black' />
              </>
            ) }
          </div>
          <div className="space-x-1 flex items-center">
            { canBulkSend && (
              <button
                type="button"
                disabled={!unsentCount || bulkSending}
                onClick={() => setBulkPrompt('send')}
                className={classNames(
                  'mr-4 inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm',
                  unsentCount && !bulkSending ? 'hover:bg-gray-50' : 'cursor-not-allowed opacity-50'
                )}
              >
                <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                Send Results ({unsentCount})
              </button>
            ) }
            <div className="block text-sm text-right font-medium text-gray-700">Auto Send Results</div>
            <SwitchToggle onChange={changeAutoSendResults} defaultChecked={survey.auto_send_results} />
          </div>
        </div>
        { rowPrompt && (
          <div role="alertdialog" aria-label="Send results" className="my-4 rounded-md border border-yellow-300 bg-yellow-50 p-4 text-sm text-gray-800">
            <p>{ rowPromptText(rowPrompt) }</p>
            <div className="mt-3 space-x-2">
              <button type="button" onClick={() => sendResults(rowPrompt)} className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700">
                Send results now
              </button>
              <button type="button" onClick={() => setRowPrompt(null)} className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">
                Not now
              </button>
            </div>
          </div>
        ) }
        { bulkPrompt && (
          <div role="alertdialog" aria-label="Send results" className="my-4 rounded-md border border-yellow-300 bg-yellow-50 p-4 text-sm text-gray-800">
            <p>{ bulkPromptText() }</p>
            <div className="mt-3 space-x-2">
              <button type="button" disabled={bulkSending} onClick={bulkSendResults} className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700">
                Send results now
              </button>
              <button type="button" disabled={bulkSending} onClick={() => setBulkPrompt(null)} className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">
                Not now
              </button>
            </div>
          </div>
        ) }
        {resultsPdfSurvey && (
          // Re-mounts when the saved list changes, so it shows what the server stored.
          // A coach added on a closed survey changes what Send Results can send,
          // and closed surveys are not polled, so the list is reloaded too.
          <CoachEmailsEditor key={(survey.coach_emails || []).join(',')} survey={survey} onSaved={() => { loadSurvey(); loadAssessments(); }} />
        )}
        <div className="mt-2 flex flex-col">
          <div className="-my-2 -mx-4 sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="shadow ring-1 ring-black ring-opacity-5">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                  <tr>
                    <AdminRequired>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        ID
                      </th>
                    </AdminRequired>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      First Name
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Last Name
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Email
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Group
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Type
                    </th>
                    {
                      surveyState.showDetailedAssessmentsView ? (
                        <>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Invite Sent
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Reminder Sent
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Opened
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Complete?
                          </th>
                          { resultsPdfSurvey && (
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Results
                            </th>
                          ) }
                        </>
                      ) : null
                    }
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Edit</span>
                    </th>
                  </tr>
                  </thead>
                  <tbody className="bg-white">
                    {assessments.map((assessment, personIdx) => {
                      const {id, group_number, participant, invite_sent, link_view_count, invite_reminders_sent, disabled, completed, assessment_type} = assessment;

                      if (id === editAssessment.id) {
                        return (
                          <tr key={participant.email} className={classNames(personIdx % 2 === 0 ? undefined : 'bg-gray-50', disabled ? 'line-through' : '')}>
                            <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                              <input className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" onBlur={(event) => updateParticipant(participant.id, event)} type="text" name="first_name" placeholder="first name" defaultValue={participant.first_name} />
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                              <input className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" onBlur={(event) => updateParticipant(participant.id, event)} type="text" name="last_name" placeholder="Last name" defaultValue={participant.last_name} />
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                              <input className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" onBlur={(event) => updateParticipant(participant.id, event)} type="email" name="email" placeholder="Email address" defaultValue={participant.email} />
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                              <input className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" onBlur={(event) => updateAssessment(id, 'group_number', event)} type="number" name="group_number" placeholder="Group number" defaultValue={group_number} />
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                              <div>
                                <select
                                  onBlur={(event) => updateAssessment(id, 'assessment_type', event)}
                                  id="assessment_type"
                                  name="assessment_type"
                                  className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                                  defaultValue={assessment_type}
                                >
                                  <option value='employee'>Employee</option>
                                  <option value='leader'>Leader</option>
                                </select>
                              </div>
                            </td>
                            <td className="relative whitespace-nowrap py-1 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              <a onClick={() => setEditAssessment({})} className="cursor-pointer text-blue-600 hover:text-blue-900">
                                Save
                              </a>
                            </td>
                          </tr>
                        )
                      }

                      return (
                        <tr key={participant.email} className={classNames(personIdx % 2 === 0 ? undefined : 'bg-gray-50', disabled ? 'line-through' : '')}>
                          <AdminRequired>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                              {assessment.id}
                            </td>
                          </AdminRequired>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            <div className="flex items-center">
                              {
                                assessment['paid?'] && (
                                  !!assessment.token
                                  ?
                                  (
                                    <Tippy content="Token purchase">
                                      <div>
                                        <CoinsIcon color="#FBCC14" className="mr-2 h-4 w-4" />
                                      </div>
                                    </Tippy>
                                  )
                                  :
                                  (
                                    <Tippy content="Cash purchase">
                                      <BanknotesIcon className="text-green-500 mr-2 h-4 w-4" />
                                    </Tippy>
                                  )
                                )
                              }
                              {participant.first_name}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{participant.last_name}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 truncate">{participant.email}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{group_number}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 capitalize">{assessment_type}</td>
                          {
                            surveyState.showDetailedAssessmentsView && (
                              <>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{invite_sent} ago</td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{invite_reminders_sent}</td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{link_view_count}</td>
                                <td className={classNames('whitespace-nowrap px-3 py-4 text-sm', !completed ? 'text-gray-500' : 'text-green-500')}>
                                  {completed ? 'Yes' : 'No'}
                                </td>
                                { resultsPdfSurvey && <ResultsDeliveryCell assessment={assessment} /> }
                              </>
                            )
                          }

                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <Menu as="div" className="relative inline-block text-left">
                              <div>
                                <Menu.Button className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                                  <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                                </Menu.Button>
                              </div>

                              <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                              >
                                <Menu.Items className="absolute right-0 z-40 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  <div className="py-1">
                                    <Menu.Item disabled={!surveyState.canEditParticipant}>
                                      {({ active, disabled}) => (
                                        <div
                                          onClick={() => setEditAssessment(assessment)}
                                          className={classNames(
                                            active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                            !disabled ? 'cursor-pointer' : 'cursor-not-allowed',
                                            'block px-4 py-2 text-sm flex space-x-2 items-center'
                                          )}
                                        >
                                          <PencilSquareIcon className="h-5 w-5 text-gray-900" />
                                          <span>Edit</span>
                                        </div>
                                      )}
                                    </Menu.Item>
                                    <Menu.Item disabled={!surveyState.canResendInvitation || completed}>
                                      {({ active, disabled }) => (
                                        <div
                                          onClick={() => resentInvitationEmail(id)}
                                          className={classNames(
                                            active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                            !disabled ? 'cursor-pointer' : 'cursor-not-allowed',
                                            'block px-4 py-2 text-sm flex space-x-2 items-center'
                                          )}
                                        >
                                          <ArrowDownCircleIcon className="h-5 w-5 text-gray-900" />
                                          <span>Resend invite email</span>
                                        </div>
                                      )}
                                    </Menu.Item>
                                    <Menu.Item disabled={!completed || !surveyState.resultDownloadsEnabled}>
                                      {({ active, disabled }) => (
                                        <div
                                          onClick={() => downloadReport(assessment)}
                                          className={classNames(
                                            active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                            !disabled ? 'cursor-pointer' : 'cursor-not-allowed',
                                            'block px-4 py-2 text-sm flex space-x-2 items-center'
                                          )}
                                        >
                                          <ArrowDownTrayIcon className="h-5 w-5 text-gray-900" />
                                          <span>Download Results</span>
                                        </div>
                                      )}
                                    </Menu.Item>
                                    <Menu.Item disabled={resultsPdfSurvey ? (!canBulkSend || !assessment.results_to_send?.length) : (!completed || !surveyState.resultDownloadsEnabled)}>
                                      {({ active, disabled }) => (
                                        <div
                                          onClick={() => !disabled && (resultsPdfSurvey ? setRowPrompt(assessment) : sendResults(assessment)) }
                                          className={classNames(
                                            active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                            !disabled ? 'cursor-pointer' : 'cursor-not-allowed',
                                            'block px-4 py-2 text-sm flex space-x-2 items-center'
                                          )}
                                        >
                                          <PaperAirplaneIcon className="h-5 w-5 text-gray-900" />
                                          <span>{ resultsPdfSurvey && assessment.results_to_send?.length && !assessment.results_to_send.includes('participant') ? 'Send coach copies' : 'Send Results' }</span>
                                        </div>
                                      )}
                                    </Menu.Item>
                                    <Menu.Item disabled={(!surveyState.canRemoveAssessment && !surveyState.canDisableAssessment) || completed}>
                                      {({ active, optionDisabled }) => (
                                        <div
                                          onClick={() => surveyState.canDisableAssessment ? disableAssessment(assessment) : removeAssessment(assessment) }
                                          className={classNames(
                                            active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                            !optionDisabled ? 'cursor-pointer' : 'cursor-not-allowed',
                                            'block px-4 py-2 text-sm flex space-x-2 items-center'
                                          )}
                                        >
                                          <NoSymbolIcon className="h-5 w-5 text-gray-900" />
                                          <span>{ surveyState.canDisableAssessment ? disabled ? 'Enable' : 'Disable' : 'Remove' }</span>
                                        </div>
                                      )}
                                    </Menu.Item>
                                    <AdminRequired>
                                      <Menu.Item>
                                        {({ active }) => (
                                          <div
                                            onClick={() => {
                                              navigator.clipboard.writeText(assessment.assessment_link).then(() => {
                                                addNotification({title: 'Copied', body: 'Assessment link copied to clipboard.'})
                                              })
                                            }}
                                            className={classNames(
                                              active ? 'bg-gray-100 text-gray-900 cursor-pointer' : 'text-gray-700',
                                              'block px-4 py-2 text-sm flex space-x-2 items-center'
                                            )}
                                          >
                                            <LinkIcon className="h-5 w-5 text-gray-900" />
                                            <div>
                                              <div>Copy Assessment Link</div>
                                              <div className="text-gray-300 text-xs">Admin only</div>
                                            </div>
                                          </div>
                                        )}
                                      </Menu.Item>
                                    </AdminRequired>
                                    {
                                      assessment.is_pointerpro && (
                                        <AdminRequired>
                                          <Menu.Item>
                                            {({ active }) => (
                                              <div
                                                onClick={() => syncAssessment(assessment)}
                                                className={classNames(
                                                  active ? 'bg-gray-100 text-gray-900 cursor-pointer' : 'text-gray-700',
                                                  'block px-4 py-2 text-sm flex space-x-2 items-center'
                                                )}
                                              >
                                                <ArrowPathIcon className="h-5 w-5 text-gray-900" />
                                                <div>
                                                  <div>Sync w/ PointerPro</div>
                                                  <div className="text-gray-300 text-xs">Admin only</div>
                                                </div>
                                              </div>
                                            )}
                                          </Menu.Item>
                                        </AdminRequired>
                                      )
                                    }
                                  </div>
                                </Menu.Items>
                              </Transition>
                            </Menu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

// Results email state per assessment (#621). A failure's reason is on the cell
// title; the exception itself is in Honeybadger under the assessment id.
function ResultsDeliveryCell({assessment: {results_status: status, results_sent: sentAgo, results_error: error, results_before_tracking: beforeTracking}}) {
  const cellClass = 'whitespace-nowrap px-3 py-4 text-sm';

  // PI ruling 2026-10-02: an old completion with a results PDF.
  if (beforeTracking) {
    return <td title="Completed before delivery tracking began. A results PDF was created; delivery was not recorded." className={classNames(cellClass, 'text-gray-500')}>Before tracking</td>;
  }
  // A sent row records when it went; should the time be missing, Sent is
  // shown without one rather than inventing it.
  if (status === 'sent') return <td className={classNames(cellClass, 'text-green-500')}>{sentAgo ? `Sent ${sentAgo} ago` : 'Sent'}</td>;
  if (status === 'pending') return <td className={classNames(cellClass, 'text-gray-500')}>Pending</td>;
  if (status === 'failed') return <td title={error} className={classNames(cellClass, 'text-red-600 font-medium')}>Failed</td>;

  return <td className={classNames(cellClass, 'text-gray-500')}>Not sent</td>;
}

function pluralEmails(count) {
  return `${count} ${count === 1 ? 'email' : 'emails'}`;
}

// Who each per-row send went, or was meant to go, to: its address, or "the
// participant" when they have none.
function recipientList(sends) {
  return sends.map((send) => send.email || 'the participant').join(', ');
}

function pluralParticipants(count) {
  return `${count} ${count === 1 ? 'participant' : 'participants'}`;
}
